/**
 * Pure H OS · Workstream 03 · R&D Laboratory
 * ===========================================
 * The development lab BEHIND Product Master. Every artifact here
 * references a `productMasterId` — R&D never invents products.
 *
 * Phase 1: Product Selector · Formula Workspace · Versioning · Approve.
 * Phase 2 (disabled): Experiments · Blind Tests · Panels · AI Insights.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FlaskConical, Beaker, ClipboardCheck, TestTube, Users, Brain,
  Plus, Save, Trash2, ArrowLeft, GitBranch, CheckCircle2, XCircle,
  Search, Info, AlertCircle, Package, ChevronRight, Lock,
} from "lucide-react";
import { Card, SectionHeader, Button, EmptyState } from "../../components/lib";
import {
  api, productMaster, PMListItem,
  rawMaterials, RawMaterial,
  rd, RDFormula, RDSummary, RDIngredient,
} from "../../services/api";

type Venture = { id: string; name: string };

// ── Small primitives ─────────────────────────────────────────────
function Pill({ tone = "neutral", children, testId }: {
  tone?: "neutral" | "success" | "warning" | "danger" | "info" | "gold";
  children: React.ReactNode; testId?: string;
}) {
  const palette = {
    neutral: { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)" },
    success: { bg: "var(--ph-success-tint)", fg: "var(--ph-success)" },
    warning: { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)" },
    danger:  { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)" },
    info:    { bg: "rgba(79,93,184,.10)",    fg: "var(--ph-ai)" },
    gold:    { bg: "var(--ph-warning-tint)", fg: "var(--ph-brand-primary)" },
  }[tone];
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded"
      style={{ background: palette.bg, color: palette.fg, letterSpacing: "0.10em", textTransform: "uppercase" }}
    >{children}</span>
  );
}

function formulaTone(status: string): "neutral" | "info" | "success" | "warning" | "danger" | "gold" {
  return ({
    draft: "neutral", active: "info", approved: "gold",
    superseded: "warning", rejected: "danger",
  } as any)[status] || "neutral";
}

function fmtCurrency(n: number | null | undefined, ccy = "EGP") {
  if (n === null || n === undefined) return "—";
  return `${ccy} ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

// ── Main component ──────────────────────────────────────────────
export default function RDLaboratory() {
  const [ventures, setVentures] = useState<Venture[]>([]);
  const [ventureId, setVentureId] = useState("orvan");
  const [products, setProducts] = useState<PMListItem[]>([]);
  const [rmLibrary, setRMLibrary] = useState<RawMaterial[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [selectedProductDoc, setSelectedProductDoc] = useState<any | null>(null);
  const [q, setQ] = useState("");
  const [rdSummary, setRDSummary] = useState<RDSummary | null>(null);
  const [formulas, setFormulas] = useState<RDFormula[]>([]);
  const [selectedFormulaId, setSelectedFormulaId] = useState<string | null>(null);
  const [tab, setTab] = useState<"formulas" | "experiments" | "blind" | "panels" | "ai">("formulas");
  const [flash, setFlash] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link pre-selection: /pureh/rd-lab?productId=pm-xxx
  useEffect(() => {
    const pid = searchParams.get("productId");
    if (pid && pid !== selectedProductId) {
      setSelectedProductId(pid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Boot
  useEffect(() => {
    (async () => {
      try {
        const [vs, rms] = await Promise.all([
          api.list("ventures").catch(() => []),
          rawMaterials.list("orvan").catch(() => []),
        ]);
        const list = ((vs as any[]) || []).map((v) => ({ id: v.id, name: v.name || v.id }));
        setVentures(list.length ? list : [{ id: "orvan", name: "ORVAN Perfumes" }]);
        setRMLibrary(rms as RawMaterial[]);
      } catch { /* ignore */ }
    })();
  }, []);

  // Load products for current venture
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const list = await productMaster.list(ventureId);
        setProducts(list);
      } finally {
        setLoading(false);
      }
    })();
  }, [ventureId]);

  // When product changes → load formulas + summary + full doc + hero image
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const refreshProduct = useCallback(async () => {
    if (!selectedProductId) { setFormulas([]); setRDSummary(null); setSelectedProductDoc(null); setHeroImageUrl(null); return; }
    const [fs, sum, doc] = await Promise.all([
      rd.listFormulas(ventureId, selectedProductId),
      rd.productSummary(ventureId, selectedProductId).catch(() => null),
      productMaster.get(ventureId, selectedProductId).catch(() => null),
    ]);
    setFormulas(fs);
    setRDSummary(sum);
    setSelectedProductDoc(doc);
    // Fetch hero image data (single asset) if the product has one bound.
    setHeroImageUrl(null);
    const heroRef = (doc as any)?.heroImageRef;
    if (heroRef) {
      try {
        const asset = await productMaster.getAsset(ventureId, selectedProductId, heroRef);
        if ((asset as any)?.dataBase64) {
          const mime = (asset as any).mime || "image/jpeg";
          setHeroImageUrl(`data:${mime};base64,${(asset as any).dataBase64}`);
        } else if ((asset as any)?.url) {
          setHeroImageUrl((asset as any).url);
        }
      } catch { /* silent fallback */ }
    }
    if (fs.length && (!selectedFormulaId || !fs.find((f) => f.id === selectedFormulaId))) {
      // Prefer approved, else latest
      const approved = fs.find((f) => f.status === "approved");
      setSelectedFormulaId((approved || fs[0]).id);
    } else if (!fs.length) {
      setSelectedFormulaId(null);
    }
  }, [ventureId, selectedProductId, selectedFormulaId]);

  useEffect(() => { refreshProduct(); }, [selectedProductId, ventureId]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 3200);
    return () => clearTimeout(t);
  }, [flash]);

  const selectedProduct = useMemo(
    () => products.find((p) => p.id === selectedProductId) || null,
    [products, selectedProductId],
  );

  const selectedFormula = useMemo(
    () => formulas.find((f) => f.id === selectedFormulaId) || null,
    [formulas, selectedFormulaId],
  );

  const productsFiltered = useMemo(() => {
    if (!q) return products;
    const needle = q.toLowerCase();
    return products.filter((p) =>
      (p.productCode || "").toLowerCase().includes(needle) ||
      (p.nameEn || "").toLowerCase().includes(needle),
    );
  }, [products, q]);

  const onCreateDraft = useCallback(async () => {
    if (!selectedProductId) return;
    try {
      const forkFrom = selectedFormula?.id;
      const created = await rd.createFormula(ventureId, {
        productMasterId: selectedProductId,
        label: forkFrom ? `Fork from ${selectedFormula?.code || "prev"}` : "Initial draft",
        forkFromFormulaId: forkFrom,
      });
      await refreshProduct();
      setSelectedFormulaId(created.id);
      setFlash(`New draft ${created.code} created`);
    } catch (e: any) {
      setFlash(e?.message || "Create failed");
    }
  }, [selectedProductId, selectedFormula, ventureId, refreshProduct]);

  const onTransition = useCallback(async (toState: string, comment?: string) => {
    if (!selectedFormula) return;
    try {
      await rd.transitionFormula(ventureId, selectedFormula.id, toState, comment);
      await refreshProduct();
      setFlash(`${selectedFormula.code} → ${toState}`);
    } catch (e: any) {
      setFlash(e?.message || `Transition failed`);
    }
  }, [selectedFormula, ventureId, refreshProduct]);

  return (
    <div className="p-6 space-y-4" data-testid="rd-laboratory">
      <SectionHeader
        eyebrow="Workstream 03 · Development Laboratory"
        title="R&D Laboratory"
        subtitle="The research workspace behind Product Master · Every formula links to one product"
      />

      {/* Venture selector */}
      <div className="flex items-center gap-3">
        <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Venture</div>
        <select
          value={ventureId}
          onChange={(e) => { setVentureId(e.target.value); setSelectedProductId(null); }}
          className="px-3 py-1.5 text-sm border rounded"
          style={{ borderColor: "var(--ph-border-strong)" }}
          data-testid="rd-venture-select"
        >
          {ventures.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <div className="ml-auto text-xs text-slate-500 flex items-center gap-2">
          <Info size={12} />
          <span>Raw Materials Library: <b>{rmLibrary.length}</b> ingredients</span>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Left · Product selector */}
        <div className="col-span-4">
          <Card padded={false} testId="rd-product-selector">
            <div className="p-3 border-b" style={{ borderColor: "var(--ph-border-subtle)" }}>
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Products</div>
              <div className="relative">
                <Search size={14} className="absolute left-2 top-2 text-slate-400" />
                <input
                  className="w-full pl-8 pr-3 py-1.5 text-sm border rounded"
                  style={{ borderColor: "var(--ph-border-strong)" }}
                  placeholder="Search by code or name…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="rd-product-search"
                />
              </div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {loading && <div className="p-4 text-xs text-slate-500">Loading…</div>}
              {!loading && !productsFiltered.length && (
                <div className="p-4 text-xs text-slate-500">No products found in this venture.</div>
              )}
              {productsFiltered.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setSelectedProductId(p.id)}
                  className="w-full text-left px-3 py-2.5 border-b hover:opacity-80 transition"
                  style={{
                    borderColor: "var(--ph-border-subtle)",
                    background: p.id === selectedProductId ? "var(--ph-surface-alt)" : "transparent",
                  }}
                  data-testid={`rd-product-${p.productCode}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900">{p.productCode}</div>
                      <div className="text-xs text-slate-600 truncate">{p.nameEn}</div>
                    </div>
                    <ChevronRight size={14} className="text-slate-400 shrink-0" />
                  </div>
                  <div className="mt-1 flex gap-1.5 flex-wrap">
                    <Pill tone="neutral">{p.launchStatus.replace("_", " ")}</Pill>
                    {p.collection && <Pill tone="info">{p.collection.replace("_", " ")}</Pill>}
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Right · Workspace */}
        <div className="col-span-8">
          {!selectedProduct && (
            <Card testId="rd-empty">
              <EmptyState
                icon={FlaskConical}
                title="Select a product to begin"
                message="R&D is anchored to Product Master. Pick a product from the left to open its formula workspace."
              />
            </Card>
          )}

          {selectedProduct && (
            <>
              {/* Product header + summary */}
              <Card testId="rd-product-header" className="mb-4">
                <div className="flex items-start gap-4">
                  {heroImageUrl ? (
                    <img
                      src={heroImageUrl}
                      alt={selectedProduct.productCode}
                      className="w-16 h-16 object-cover rounded shrink-0"
                      style={{ border: "1px solid var(--ph-border-subtle)" }}
                      data-testid="rd-product-hero"
                    />
                  ) : (
                    <div
                      className="w-16 h-16 rounded flex items-center justify-center shrink-0"
                      style={{ background: "var(--ph-surface-alt)", border: "1px solid var(--ph-border-subtle)" }}
                      data-testid="rd-product-hero-empty"
                    >
                      <Package size={20} className="opacity-40" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="text-lg font-bold" style={{ letterSpacing: "0.06em" }}>
                        {selectedProduct.productCode}
                      </div>
                      <Pill tone="info">{selectedProduct.launchStatus.replace("_", " ")}</Pill>
                      {rdSummary?.approved && <Pill tone="gold" testId="rd-approved-pill">APPROVED · v{rdSummary.approved.version}</Pill>}
                    </div>
                    <div className="text-sm text-slate-600 mt-0.5">{selectedProduct.nameEn}</div>
                  </div>
                  <Button variant="primary" onClick={onCreateDraft} testId="rd-new-draft-btn">
                    <Plus size={14} /> {selectedFormula ? "Fork new draft" : "Create first formula"}
                  </Button>
                </div>

                <div className="grid grid-cols-4 gap-3 mt-3 pt-3 border-t" style={{ borderColor: "var(--ph-border-subtle)" }}>
                  <div data-testid="rd-header-product-name">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Product name</div>
                    <div className="text-sm font-bold text-slate-900 mt-0.5 truncate">
                      {selectedProductDoc?.nameEn || selectedProduct.nameEn || "—"}
                    </div>
                    {selectedProductDoc?.nameAr && (
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{selectedProductDoc.nameAr}</div>
                    )}
                  </div>
                  <div data-testid="rd-header-readiness">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Readiness</div>
                    <div className="text-sm font-bold text-slate-900 mt-0.5">
                      {typeof selectedProductDoc?.readiness?.score === "number"
                        ? <>{selectedProductDoc.readiness.score}<span className="text-xs text-slate-500">/100</span></>
                        : "—"}
                    </div>
                  </div>
                  <div data-testid="rd-header-launch">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Launch status</div>
                    <div className="mt-0.5">
                      <Pill tone={selectedProduct.launchStatus === "ready_to_sell" ? "gold" :
                                     selectedProduct.launchStatus === "active" ? "success" :
                                     selectedProduct.launchStatus === "discontinued" ? "danger" : "info"}>
                        {selectedProduct.launchStatus.replace("_", " ")}
                      </Pill>
                    </div>
                  </div>
                  <div data-testid="rd-header-product-version">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Product version</div>
                    <div className="text-sm font-bold text-slate-900 mt-0.5">
                      v{selectedProductDoc?.version ?? 1}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3 mt-3 pt-3 border-t" style={{ borderColor: "var(--ph-border-subtle)" }}>
                  <SummaryTile label="Total formulas" value={rdSummary?.totalFormulas ?? 0} />
                  <SummaryTile label="Draft"          value={rdSummary?.counts?.draft ?? 0} />
                  <SummaryTile label="Approved"       value={rdSummary?.counts?.approved ?? 0} tone="gold" />
                  <SummaryTile label="Superseded"     value={rdSummary?.counts?.superseded ?? 0} />
                </div>
              </Card>

              {/* Tabs */}
              <div className="flex gap-1 border-b overflow-x-auto mb-4" style={{ borderColor: "var(--ph-border-subtle)" }}>
                {[
                  { key: "formulas",     label: "Formulas",       Icon: Beaker,         enabled: true },
                  { key: "experiments",  label: "Experiments",    Icon: TestTube,       enabled: false },
                  { key: "blind",        label: "Blind Tests",    Icon: FlaskConical,   enabled: false },
                  { key: "panels",       label: "Panels",         Icon: Users,          enabled: false },
                  { key: "ai",           label: "AI Insights",    Icon: Brain,          enabled: false },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => t.enabled && setTab(t.key as any)}
                    disabled={!t.enabled}
                    data-testid={`rd-tab-${t.key}`}
                    className="px-4 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition"
                    style={{
                      borderColor: tab === t.key ? "var(--ph-brand-primary)" : "transparent",
                      color: !t.enabled ? "var(--ph-ink-muted)" : (tab === t.key ? "var(--ph-brand-primary)" : "var(--ph-ink)"),
                      opacity: t.enabled ? 1 : 0.5,
                      cursor: t.enabled ? "pointer" : "not-allowed",
                    }}
                  >
                    <t.Icon size={12} /> {t.label}
                    {!t.enabled && <Lock size={10} />}
                  </button>
                ))}
              </div>

              {tab === "formulas" && (
                <FormulasTab
                  ventureId={ventureId}
                  formulas={formulas}
                  selectedFormula={selectedFormula}
                  selectedFormulaId={selectedFormulaId}
                  onSelect={setSelectedFormulaId}
                  onTransition={onTransition}
                  rmLibrary={rmLibrary}
                  onRefresh={refreshProduct}
                  onFlash={setFlash}
                />
              )}
              {tab !== "formulas" && (
                <Card testId="rd-phase2-placeholder">
                  <div className="text-center py-10">
                    <Lock size={32} className="mx-auto opacity-40 mb-3" />
                    <div className="text-sm font-semibold text-slate-900">Coming in Phase 2</div>
                    <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                      Experiments, Blind Tests, Panel Sessions and AI Insights will ship after Phase 1
                      (Formulas + Approve) is validated with real ORVAN data. The workflow is intentionally
                      visible so you can see the complete R&amp;D journey we&apos;re building toward.
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {flash && (
        <div
          className="fixed bottom-6 right-6 px-4 py-2 rounded shadow-lg z-50"
          style={{ background: "var(--ph-brand-primary, #1a1a1a)", color: "#F7F3EB", fontSize: 12 }}
          data-testid="rd-flash"
        >{flash}</div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, tone = "neutral" }: { label: string; value: number; tone?: "neutral" | "gold" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</div>
      <div className={`text-xl font-bold mt-0.5 ${tone === "gold" ? "" : "text-slate-900"}`}
           style={tone === "gold" ? { color: "var(--ph-brand-primary)" } : undefined}>
        {value}
      </div>
    </div>
  );
}

// ── Formulas tab ────────────────────────────────────────────────
function FormulasTab({
  ventureId, formulas, selectedFormula, selectedFormulaId, onSelect,
  onTransition, rmLibrary, onRefresh, onFlash,
}: {
  ventureId: string;
  formulas: RDFormula[];
  selectedFormula: RDFormula | null;
  selectedFormulaId: string | null;
  onSelect: (id: string) => void;
  onTransition: (to: string, comment?: string) => void;
  rmLibrary: RawMaterial[];
  onRefresh: () => Promise<void>;
  onFlash: (m: string) => void;
}) {
  const editable = selectedFormula && !["approved", "superseded"].includes(selectedFormula.status);

  if (!formulas.length) {
    return (
      <Card testId="rd-formulas-empty">
        <EmptyState
          icon={Beaker}
          title="No formula versions yet"
          message="Click 'Create first formula' at the top-right to start development."
        />
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      {/* Version list */}
      <div className="col-span-4">
        <Card padded={false} testId="rd-version-list">
          <div className="p-3 border-b" style={{ borderColor: "var(--ph-border-subtle)" }}>
            <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold flex items-center gap-1.5">
              <GitBranch size={12} /> Formula versions
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {formulas.map((f) => (
              <button
                key={f.id}
                onClick={() => onSelect(f.id)}
                className="w-full text-left px-3 py-2 border-b hover:opacity-80"
                style={{
                  borderColor: "var(--ph-border-subtle)",
                  background: f.id === selectedFormulaId ? "var(--ph-surface-alt)" : "transparent",
                }}
                data-testid={`rd-version-${f.version}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold" style={{ letterSpacing: "0.06em" }}>
                    v{f.version} · {f.code}
                  </div>
                  <Pill tone={formulaTone(f.status)}>{f.status}</Pill>
                </div>
                {f.label && <div className="text-xs text-slate-500 mt-0.5 truncate">{f.label}</div>}
                <div className="text-[11px] text-slate-500 mt-1">
                  {f.ingredients.length} ingredients · {f.totalGrams}g · {fmtCurrency(f.costPerBatch)}
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Editor + Approve */}
      <div className="col-span-8">
        {selectedFormula && (
          <FormulaEditor
            ventureId={ventureId}
            formula={selectedFormula}
            rmLibrary={rmLibrary}
            editable={!!editable}
            onTransition={onTransition}
            onRefresh={onRefresh}
            onFlash={onFlash}
          />
        )}
      </div>
    </div>
  );
}

function FormulaEditor({
  ventureId, formula, rmLibrary, editable, onTransition, onRefresh, onFlash,
}: {
  ventureId: string;
  formula: RDFormula;
  rmLibrary: RawMaterial[];
  editable: boolean;
  onTransition: (to: string, comment?: string) => void;
  onRefresh: () => Promise<void>;
  onFlash: (m: string) => void;
}) {
  const [ingredients, setIngredients] = useState<RDIngredient[]>(formula.ingredients);
  const [notes, setNotes] = useState(formula.notes || "");
  const [label, setLabel] = useState(formula.label || "");
  const [maceration, setMaceration] = useState<number | null>(formula.macerationDays ?? null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setIngredients(formula.ingredients);
    setNotes(formula.notes || "");
    setLabel(formula.label || "");
    setMaceration(formula.macerationDays ?? null);
  }, [formula.id]);

  const totalG = ingredients.reduce((s, r) => s + Number(r.grams || 0), 0);

  const updateRow = (idx: number, patch: Partial<RDIngredient>) => {
    setIngredients((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const addRow = () => {
    setIngredients((rows) => [
      ...rows,
      { rawMaterialId: rmLibrary[0]?.id || "", grams: 0, orderIndex: rows.length },
    ]);
  };
  const removeRow = (idx: number) => {
    setIngredients((rows) => rows.filter((_, i) => i !== idx));
  };

  const onSave = useCallback(async () => {
    setSaving(true);
    try {
      await rd.patchFormula(ventureId, formula.id, { ingredients, notes, label, macerationDays: maceration ?? undefined });
      await onRefresh();
      onFlash("Formula saved");
    } catch (e: any) {
      onFlash(e?.message || "Save failed");
    } finally { setSaving(false); }
  }, [ventureId, formula.id, ingredients, notes, label, maceration, onRefresh, onFlash]);

  const onApprove = () => {
    if (formula.status === "draft") onTransition("active");
    else if (formula.status === "active") {
      
      if (window.confirm(
        `Approve ${formula.code} v${formula.version}?\n\n` +
        `This will:\n` +
        `  • Lock the formula (no further edits)\n` +
        `  • Update Product Master with this approved formula\n` +
        `  • Supersede any previously approved formula for this product`,
      )) onTransition("approved");
    }
  };
  const onDelete = () => {
    
    if (window.confirm(`Delete ${formula.code}? This cannot be undone.`)) {
      rd.deleteFormula(ventureId, formula.id).then(() => onRefresh()).then(() => onFlash("Deleted"));
    }
  };

  return (
    <Card testId="rd-formula-editor">
      {/* Header row */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="text-lg font-bold" style={{ letterSpacing: "0.06em" }}>
              {formula.code} · v{formula.version}
            </div>
            <Pill tone={formulaTone(formula.status)}>{formula.status}</Pill>
            {formula.parentFormulaId && (
              <span className="text-[11px] text-slate-500 flex items-center gap-1">
                <GitBranch size={10} /> forked
              </span>
            )}
          </div>
          <input
            className="mt-1 text-sm w-full border-0 focus:outline-none bg-transparent"
            placeholder="Label (e.g. 'Iteration two — more oud')"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={!editable}
            data-testid="rd-label-input"
          />
        </div>
        <div className="flex gap-2">
          {editable && (
            <>
              <Button variant="ghost" onClick={onDelete} testId="rd-delete-btn"><Trash2 size={14} /> Delete</Button>
              <Button variant="primary" onClick={onSave} disabled={saving} testId="rd-save-btn">
                <Save size={14} /> {saving ? "Saving…" : "Save"}
              </Button>
            </>
          )}
          {formula.status === "draft" && (
            <Button variant="primary" onClick={onApprove} testId="rd-activate-btn">
              <CheckCircle2 size={14} /> Move to Active
            </Button>
          )}
          {formula.status === "active" && (
            <Button variant="primary" onClick={onApprove} testId="rd-approve-btn">
              <Lock size={14} /> Approve & lock
            </Button>
          )}
        </div>
      </div>

      {formula.status === "approved" && (
        <div
          className="mb-3 px-3 py-2 rounded text-xs flex items-center gap-2"
          style={{ background: "var(--ph-warning-tint)", color: "var(--ph-brand-primary)" }}
        >
          <Lock size={12} />
          <span>This formula is <b>approved & locked</b>. Product Master is bound to it. Fork a new draft to iterate further.</span>
        </div>
      )}
      {formula.status === "superseded" && (
        <div
          className="mb-3 px-3 py-2 rounded text-xs flex items-center gap-2"
          style={{ background: "var(--ph-neutral-tint)", color: "var(--ph-ink-muted)" }}
        >
          <XCircle size={12} />
          <span>This version has been <b>superseded</b> by a newer approved formula.</span>
        </div>
      )}

      {/* Cost strip */}
      <div className="grid grid-cols-4 gap-3 py-3 mb-3 border-y" style={{ borderColor: "var(--ph-border-subtle)" }}>
        <SummaryTile label="Ingredients"  value={ingredients.length} />
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Total mass</div>
          <div className="text-xl font-bold text-slate-900 mt-0.5">{totalG.toFixed(1)}<span className="text-xs text-slate-500 ml-1">g</span></div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Cost / batch</div>
          <div className="text-xl font-bold text-slate-900 mt-0.5">{fmtCurrency(formula.costPerBatch)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Cost / mL</div>
          <div className="text-xl font-bold text-slate-900 mt-0.5">{fmtCurrency(formula.costPerMl)}</div>
        </div>
      </div>

      {/* Ingredients table */}
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1.5">Ingredients</div>
      <div className="overflow-x-auto" data-testid="rd-ingredients-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500">
              <th className="pb-2 font-semibold" style={{ width: "50%" }}>Raw Material</th>
              <th className="pb-2 font-semibold text-right">Grams</th>
              <th className="pb-2 font-semibold text-right">% of total</th>
              <th className="pb-2 font-semibold">Note</th>
              <th className="pb-2"></th>
            </tr>
          </thead>
          <tbody>
            {ingredients.map((row, idx) => {
              const rm = rmLibrary.find((r) => r.id === row.rawMaterialId);
              const pct = totalG > 0 ? ((Number(row.grams || 0) / totalG) * 100) : 0;
              return (
                <tr key={idx} className="border-t" style={{ borderColor: "var(--ph-border-subtle)" }}>
                  <td className="py-2">
                    <select
                      value={row.rawMaterialId}
                      onChange={(e) => updateRow(idx, { rawMaterialId: e.target.value })}
                      disabled={!editable}
                      className="w-full text-sm px-2 py-1 border rounded"
                      style={{ borderColor: "var(--ph-border-strong)" }}
                      data-testid={`rd-ing-rm-${idx}`}
                    >
                      {rmLibrary.map((r) => (
                        <option key={r.id} value={r.id}>{`${r.code} · ${r.name}`}</option>
                      ))}
                    </select>
                    {rm && (
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {rm.category.replace("_", " ")}
                        {rm.ifraStatus !== "unknown" && ` · IFRA ${rm.ifraStatus}`}
                        {rm.costPerUnit !== null && rm.costPerUnit !== undefined && ` · ${rm.currency} ${rm.costPerUnit}/${rm.unit}`}
                      </div>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <input
                      type="number"
                      step="0.01"
                      value={row.grams}
                      onChange={(e) => updateRow(idx, { grams: Number(e.target.value) })}
                      disabled={!editable}
                      className="w-24 text-right text-sm px-2 py-1 border rounded"
                      style={{ borderColor: "var(--ph-border-strong)" }}
                      data-testid={`rd-ing-grams-${idx}`}
                    />
                  </td>
                  <td className="py-2 text-right text-sm text-slate-600">
                    {pct.toFixed(2)}%
                  </td>
                  <td className="py-2">
                    <input
                      type="text"
                      value={row.note || ""}
                      onChange={(e) => updateRow(idx, { note: e.target.value })}
                      disabled={!editable}
                      className="w-full text-sm px-2 py-1 border rounded"
                      style={{ borderColor: "var(--ph-border-strong)" }}
                      placeholder="top / heart / base / …"
                    />
                  </td>
                  <td className="py-2 text-right">
                    {editable && (
                      <button onClick={() => removeRow(idx)} className="text-slate-400 hover:text-red-600"
                              data-testid={`rd-ing-remove-${idx}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {editable && (
          <button
            onClick={addRow}
            className="mt-2 px-3 py-1.5 text-xs font-semibold rounded border border-dashed hover:opacity-70"
            style={{ borderColor: "var(--ph-border-strong)", color: "var(--ph-ink-muted)" }}
            data-testid="rd-ing-add"
          >
            <Plus size={12} className="inline mr-1" /> Add ingredient
          </button>
        )}
      </div>

      {/* Meta */}
      <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t" style={{ borderColor: "var(--ph-border-subtle)" }}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Notes</div>
          <textarea
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!editable}
            className="w-full text-sm px-3 py-2 border rounded"
            style={{ borderColor: "var(--ph-border-strong)" }}
            data-testid="rd-notes"
            placeholder="Composition notes, olfactive intent, next iteration ideas…"
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">Maceration (days)</div>
          <input
            type="number"
            value={maceration ?? ""}
            onChange={(e) => setMaceration(e.target.value === "" ? null : Number(e.target.value))}
            disabled={!editable}
            className="w-32 text-sm px-3 py-2 border rounded"
            style={{ borderColor: "var(--ph-border-strong)" }}
            data-testid="rd-maceration"
          />
          <div className="text-[10px] text-slate-500 mt-1">
            Concentration target: <b>{formula.concentrationTarget || "—"}</b>
          </div>
        </div>
      </div>
    </Card>
  );
}
